import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  getReservationCancellationReasonDisplay,
  OtherBookingParticipant,
  OtherBookingReservation,
  ReservationBookingStatus,
  ReservationDetails,
  ReservationDetailsPart,
  ReservationPartDetails,
  ScheduledEvent,
  ScheduledEventReservation
} from '../../../../../../models/scheduled-event';
import { ScheduledEventService } from '../../../../../../services/scheduled-event/scheduled-event.service';
import {
  dateStringFromISO8601,
  getAge,
  propExistsAndIsNonEmpty,
  timeStringFromISO8601
} from '../../../../../../utils';
import { getDetailsEventLink } from '../event-details-link';
import { formatQuestionAnswer, getQuestionNames } from '../question-responses';
import {
  BOOKING_DETAILS_QUERY_PARAM,
  RESERVATION_DETAILS_QUERY_PARAM
} from '../details-modal-query-params';

// The query parameter used to deep-link a participant details modal. When this
// is present in the URL, the participants sub-list will open the corresponding
// modal on load.
export const PARTICIPANT_DETAILS_QUERY_PARAM = 'participantReservationPartId';

export { RESERVATION_DETAILS_QUERY_PARAM };

@Component({
  selector: 'app-participant-details-modal',
  templateUrl: './participant-details-modal.component.html',
  styleUrls: ['./participant-details-modal.component.css']
})
export class ParticipantDetailsModalComponent implements OnInit {

  // The reservation part whose details are shown, in the event-page mode.
  @Input() reservationPartId: number;
  // The whole reservation whose details are shown, in the reservation mode
  // (PRO-1514). Set instead of reservationPartId; the event and the footer
  // actions are then not supplied, and the payload carries the event itself.
  @Input() reservationId: number;
  // The reservation row from the participants table, used for the footer action
  // button visibility conditions and passed back to the action callbacks.
  @Input() reservation: ScheduledEventReservation;
  // The scheduled event being viewed, used for the event part info and footer
  // button visibility conditions.
  @Input() event: ScheduledEvent;
  // The event detail URL path (without query string), used to build links to
  // the other participants' modals.
  @Input() currentPath: string;

  // Footer action callbacks, supplied by the participants sub-list. Each wraps
  // the equivalent action in the participants table.
  @Input() toggleCheckInCallback: () => void;
  @Input() completePaymentCallback: () => void;
  @Input() rebookCallback: () => void;
  @Input() cancelCallback: () => void;

  public details: ReservationPartDetails | ReservationDetails = null;
  public loading = false;
  public serverError: number = null;

  public readonly getAge = getAge;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly propExistsAndIsNonEmpty = propExistsAndIsNonEmpty;
  public readonly getQuestionNames = getQuestionNames;
  public readonly formatQuestionAnswer = formatQuestionAnswer;

  constructor(
    public activeModal: NgbActiveModal,
    private scheduledEventService: ScheduledEventService
  ) {}

  ngOnInit() {
    this.loadDetails();
  }

  /**
   * Whether the modal was opened for a whole reservation rather than for one
   * session of it on an event page. The two modes differ in what they load,
   * where the event comes from, and whether the footer actions apply.
   */
  public isReservationMode(): boolean {
    return !this.reservationPartId && !!this.reservationId;
  }

  public loadDetails() {
    this.loading = true;
    this.serverError = null;

    const request: Promise<ReservationPartDetails | ReservationDetails> =
      this.isReservationMode()
        ? this.scheduledEventService.getReservationDetails(this.reservationId)
        : this.scheduledEventService.getReservationPartDetails(this.reservationPartId);

    request
      .then(details => {
        this.details = details;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loading = false; });
  }

  /** The reservation-mode payload, for the fields only it carries. */
  public getReservationDetails(): ReservationDetails {
    return this.details as ReservationDetails;
  }

  /**
   * The event details page, or null when the event cannot be addressed. Only
   * the reservation mode has the event fields; the event-page mode reaches its
   * event through the `event` input instead.
   */
  public getEventDetailsLink(): string | null {
    if (!this.isReservationMode()) {
      return null;
    }
    return getDetailsEventLink(this.getReservationDetails());
  }

  public getParts(): ReservationDetailsPart[] {
    return this.getReservationDetails().parts;
  }

  public getOtherReservations(): OtherBookingReservation[] {
    return this.getReservationDetails().other_participants;
  }

  public getOtherParticipants(): OtherBookingParticipant[] {
    return (this.details as ReservationPartDetails).other_participants;
  }

  /** The zone the event's times are shown in, whichever mode supplied them. */
  public getTimeZone(): string {
    return this.isReservationMode()
      ? this.getReservationDetails().time_zone
      : this.event.time_zone;
  }

  public isReservationCancelled(): boolean {
    return !!this.details && (
      this.details.is_cancelled ||
      this.details.booking_status === ReservationBookingStatus.cancelled
    );
  }

  public getReservationStatusDisplay(): string {
    if (!this.details) {
      return '';
    }
    if (this.isReservationCancelled()) {
      return 'Cancelled';
    }
    switch (this.details.booking_status) {
      case ReservationBookingStatus.depositPaid:
        return 'Deposit Paid';
      case ReservationBookingStatus.complete:
        return 'Payment Complete';
      case ReservationBookingStatus.incomplete:
        return 'Incomplete';
      default:
        return '';
    }
  }

  public getCancellationReasonDisplay(): string {
    if (!this.details) {
      return '';
    }
    return getReservationCancellationReasonDisplay(
      this.details.cancellation_reason
    );
  }

  /** Null when the payload predates includes_entry, so the item renders nothing. */
  public getIncludesEntryDisplay(): 'Yes' | 'No' | null {
    if (!this.details || this.details.includes_entry === undefined || this.details.includes_entry === null) {
      return null;
    }
    return this.details.includes_entry ? 'Yes' : 'No';
  }

  public getOtherParticipantQueryParams(participant: OtherBookingParticipant): { [key: string]: number } {
    return { [PARTICIPANT_DETAILS_QUERY_PARAM]: participant.reservation_part_id };
  }

  public getOtherReservationQueryParams(participant: OtherBookingReservation): { [key: string]: number } {
    return { [RESERVATION_DETAILS_QUERY_PARAM]: participant.reservation_id };
  }

  public getBookingQueryParams(): { [key: string]: number } {
    return { [BOOKING_DETAILS_QUERY_PARAM]: this.details.booking_id };
  }

  // Footer actions. Each closes the modal then performs the same action as the
  // equivalent button in the participants table.
  public handleToggleCheckIn() {
    this.activeModal.close();
    this.toggleCheckInCallback();
  }

  /**
   * PRO-1388: the footer's "Complete Payment" anchor points at the booking's
   * tokenized, login-free payment URL so it can be right-clicked and copied.
   * A plain left-click is intercepted and handed to the participants sub-list,
   * which opens the page through the login-token flow so the staff session --
   * and with it the card-reader option -- is present. Modified clicks fall
   * through to the browser with the plain token URL, leaving the modal open.
   */
  public handleCompletePayment(clickEvent: MouseEvent) {
    if (clickEvent.ctrlKey || clickEvent.metaKey || clickEvent.shiftKey ||
        clickEvent.altKey || (clickEvent.button !== 0)) {
      return;
    }
    clickEvent.preventDefault();

    this.activeModal.close();
    this.completePaymentCallback();
  }

  public handleRebook() {
    this.activeModal.close();
    this.rebookCallback();
  }

  public handleCancel() {
    this.activeModal.close();
    this.cancelCallback();
  }
}
